// Inbox (SPEC §4): conversation list from v_message_inbox — unread bold,
// unmatched-number rows shown with the phone + a note and a "Link to client"
// action (assign_message_client RPC, migration 006) — plus a thread pane using
// MessageThread for matched clients. Polls every 15s.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useQuery } from "../lib/useQuery";
import { fmtDate, fmtDateTime } from "../lib/dates";
import { phoneDisplay, telHref } from "../lib/format";
import Modal from "../components/Modal";
import MessageThread from "../components/MessageThread";
import type { Id, InboxRow, Message } from "../lib/types";

const POLL_MS = 15_000;

type Selection =
  | { kind: "client"; id: Id; name: string }
  | { kind: "phone"; phone: string };

/** The phone for an unmatched inbox row, whichever column the view exposes. */
function rowPhone(row: InboxRow): string | null {
  return row.from_phone ?? row.phone_e164 ?? null;
}

function rowKey(row: InboxRow): string {
  return row.client_id != null ? `c:${String(row.client_id)}` : `p:${rowPhone(row) ?? "unknown"}`;
}

function isSelected(row: InboxRow, sel: Selection | null): boolean {
  if (!sel) return false;
  if (sel.kind === "client") return row.client_id != null && String(row.client_id) === String(sel.id);
  return row.client_id == null && rowPhone(row) === sel.phone;
}

/** Compact "now / 5m / 3h / 2d / Aug 4, 2026" timestamp for the list column. */
function shortWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const secs = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (secs < 60) return "now";
  if (secs < 3600) return `${Math.floor(secs / 60)}m`;
  if (secs < 86_400) return `${Math.floor(secs / 3600)}h`;
  if (secs < 7 * 86_400) return `${Math.floor(secs / 86_400)}d`;
  return fmtDate(iso);
}

const digitsOf = (s: string | null | undefined): string => (s ?? "").replace(/\D/g, "");

interface ClientPickRow {
  id: Id;
  full_name: string | null;
  phone_e164: string | null;
  email: string | null;
}

/**
 * "Link to client" modal for an unmatched number: pick a client, then
 * assign_message_client repoints that number's unmatched messages to them.
 */
function LinkClientModal({
  phone,
  open,
  onClose,
  onLinked,
}: {
  phone: string;
  open: boolean;
  onClose: () => void;
  onLinked: (client: ClientPickRow) => void;
}) {
  const [search, setSearch] = useState("");
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  // Fallback search straight against `clients` when v_client_list yields
  // nothing — internal-tagged accounts (nurses, test numbers) are hidden from
  // the view but are still valid link targets.
  const [fallback, setFallback] = useState<{
    rows: ClientPickRow[];
    loading: boolean;
    error: string | null;
  }>({ rows: [], loading: false, error: null });

  const { data: clients, error, loading } = useQuery<ClientPickRow[]>(async () => {
    if (!open) return [];
    const { data, error: qErr } = await supabase
      .from("v_client_list")
      .select("id, full_name, phone_e164, email")
      .order("full_name", { ascending: true });
    if (qErr) throw new Error(qErr.message);
    return (data ?? []) as ClientPickRow[];
  }, [open]);

  useEffect(() => {
    if (open) {
      setSearch("");
      setLinkError(null);
    }
  }, [open]);

  const matches = useMemo(() => {
    const all = clients ?? [];
    const q = search.trim().toLowerCase();
    const qDigits = digitsOf(q);
    const filtered = q
      ? all.filter(
          (c) =>
            (c.full_name ?? "").toLowerCase().includes(q) ||
            (c.email ?? "").toLowerCase().includes(q) ||
            (qDigits.length >= 3 && digitsOf(c.phone_e164).includes(qDigits)),
        )
      : all;
    // Clients whose saved phone matches the unmatched number float to the top.
    const target = digitsOf(phone);
    return [...filtered]
      .sort((a, b) => {
        const am = target.length > 0 && digitsOf(a.phone_e164).endsWith(target.slice(-10)) ? 0 : 1;
        const bm = target.length > 0 && digitsOf(b.phone_e164).endsWith(target.slice(-10)) ? 0 : 1;
        return am - bm || (a.full_name ?? "").localeCompare(b.full_name ?? "");
      })
      .slice(0, 30);
  }, [clients, search, phone]);

  // When the v_client_list search comes up empty, search the clients table
  // directly (debounced) so unmatched numbers can be linked to internal
  // accounts too. Merged-away rows stay excluded.
  useEffect(() => {
    if (!open || loading || matches.length > 0) {
      setFallback({ rows: [], loading: false, error: null });
      return;
    }
    let cancelled = false;
    setFallback({ rows: [], loading: true, error: null });
    const t = setTimeout(() => {
      void (async () => {
        try {
          // Strip characters that would break PostgREST's .or() filter syntax.
          const text = search.trim().replace(/[%_,().\\]/g, "").trim();
          const qDigits = digitsOf(search);
          const ors: string[] = [];
          if (text) {
            ors.push(
              `first_name.ilike.%${text}%`,
              `last_name.ilike.%${text}%`,
              `email.ilike.%${text}%`,
            );
          }
          if (qDigits.length >= 3) ors.push(`phone_e164.ilike.%${qDigits}%`);
          if (ors.length === 0) {
            // No usable query text: try the thread's own number.
            const tail = digitsOf(phone).slice(-10);
            if (tail.length >= 3) ors.push(`phone_e164.ilike.%${tail}%`);
          }
          if (ors.length === 0) {
            if (!cancelled) setFallback({ rows: [], loading: false, error: null });
            return;
          }
          const { data, error: qErr } = await supabase
            .from("clients")
            .select("id, first_name, last_name, phone_e164, email")
            .is("merged_into_client_id", null)
            .or(ors.join(","))
            .limit(30);
          if (qErr) throw new Error(qErr.message);
          const rows: ClientPickRow[] = (
            (data ?? []) as {
              id: Id;
              first_name: string | null;
              last_name: string | null;
              phone_e164: string | null;
              email: string | null;
            }[]
          ).map((c) => ({
            id: c.id,
            full_name: [c.first_name, c.last_name].filter(Boolean).join(" ") || null,
            phone_e164: c.phone_e164,
            email: c.email,
          }));
          if (!cancelled) setFallback({ rows, loading: false, error: null });
        } catch (e) {
          if (!cancelled)
            setFallback({
              rows: [],
              loading: false,
              error: e instanceof Error ? e.message : String(e),
            });
        }
      })();
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [open, loading, matches, search, phone]);

  async function link(client: ClientPickRow) {
    if (linking) return;
    setLinking(true);
    setLinkError(null);
    try {
      const { error: rpcErr } = await supabase.rpc("assign_message_client", {
        p_phone: phone,
        p_client_id: client.id,
      });
      if (rpcErr) {
        const hint = /could not find the function|pgrst202/i.test(rpcErr.message)
          ? " (apply supabase/migrations/006_messaging_extras.sql)"
          : "";
        throw new Error(rpcErr.message + hint);
      }
      onLinked(client);
    } catch (e) {
      setLinkError(e instanceof Error ? e.message : String(e));
    } finally {
      setLinking(false);
    }
  }

  function pickButton(c: ClientPickRow, internal: boolean) {
    return (
      <button
        key={String(c.id)}
        type="button"
        className="inbox-row"
        disabled={linking}
        onClick={() => void link(c)}
      >
        <div className="inbox-main">
          <div className="inbox-name">{c.full_name ?? `Client ${String(c.id)}`}</div>
          <div className="inbox-preview">
            {[phoneDisplay(c.phone_e164), c.email].filter((v) => v && v !== "—").join(" · ")}
          </div>
        </div>
        {internal && <span className="badge badge-muted">Internal</span>}
        {digitsOf(c.phone_e164).length > 0 &&
          digitsOf(phone).length > 0 &&
          digitsOf(c.phone_e164).endsWith(digitsOf(phone).slice(-10)) && (
            <span className="badge badge-green">Phone match</span>
          )}
      </button>
    );
  }

  return (
    <Modal open={open} title={`Link ${phoneDisplay(phone)} to a client`} onClose={onClose}>
      <p className="muted small">
        All unmatched messages from this number will be attached to the client you pick. Clients
        whose saved phone matches are listed first.
      </p>
      {linkError && <div className="error-banner">Link failed: {linkError}</div>}
      {error && <div className="error-banner">Failed to load clients: {error}</div>}
      <div className="field">
        <label htmlFor="link-client-search">Search clients</label>
        <input
          id="link-client-search"
          type="search"
          value={search}
          placeholder="Name, phone, or email…"
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
        />
      </div>
      {loading ? (
        <div className="loading">
          <span className="spinner" /> Loading clients…
        </div>
      ) : matches.length > 0 ? (
        <div className="inbox-list">{matches.map((c) => pickButton(c, false))}</div>
      ) : fallback.loading ? (
        <div className="loading">
          <span className="spinner" /> Searching all clients…
        </div>
      ) : fallback.rows.length > 0 ? (
        <>
          <div className="muted small mb-1">
            No matches in the client list — showing hidden internal accounts:
          </div>
          <div className="inbox-list">{fallback.rows.map((c) => pickButton(c, true))}</div>
        </>
      ) : (
        <div className="empty-state">
          No matching clients
          {fallback.error ? ` (searching all clients failed: ${fallback.error})` : ""}.
        </div>
      )}
    </Modal>
  );
}

/** Thread pane for a matched client: header card + MessageThread. */
function ClientPane({ clientId, name }: { clientId: Id; name: string }) {
  const { data: client, error, loading } = useQuery<{
    phone_e164: string | null;
    sms_opt_out: boolean;
  }>(async () => {
    const { data, error: qErr } = await supabase
      .from("clients")
      .select("phone_e164, sms_opt_out")
      .eq("id", clientId)
      .single();
    if (qErr) throw new Error(qErr.message);
    return data as { phone_e164: string | null; sms_opt_out: boolean };
  }, [clientId]);

  return (
    <div className="panel">
      <div className="page-header" style={{ marginBottom: 8 }}>
        <div>
          <h2 style={{ margin: 0 }}>
            <Link to={`/clients/${String(clientId)}`}>{name}</Link>
          </h2>
          <div className="page-sub">
            {client?.phone_e164 ? (
              <a href={telHref(client.phone_e164)}>{phoneDisplay(client.phone_e164)}</a>
            ) : (
              "No phone on file"
            )}
          </div>
        </div>
        {client?.sms_opt_out && <span className="badge badge-red">Opted out</span>}
      </div>
      {error && <div className="error-banner">Failed to load client: {error}</div>}
      {loading && !client ? (
        <div className="loading">
          <span className="spinner" /> Loading…
        </div>
      ) : (
        <MessageThread
          clientId={clientId}
          phone={client?.phone_e164 ?? null}
          optedOut={client?.sms_opt_out ?? false}
        />
      )}
    </div>
  );
}

/**
 * Thread pane for an unmatched number: read-only bubbles (replying requires a
 * client for sendSms) + the "Link to client" action.
 */
function UnmatchedPane({
  phone,
  onLinked,
  onRead,
}: {
  phone: string;
  onLinked: (client: ClientPickRow) => void;
  /** Called after unmatched messages are marked read, so the inbox list refreshes. */
  onRead: () => void;
}) {
  const [linkOpen, setLinkOpen] = useState(false);
  const [markingRead, setMarkingRead] = useState(false);
  const [markError, setMarkError] = useState<string | null>(null);

  const { data: messages, error, loading, reload } = useQuery<Message[]>(async () => {
    const { data, error: qErr } = await supabase
      .from("messages")
      .select("*")
      .is("client_id", null)
      .eq("from_phone", phone)
      .order("created_at", { ascending: true })
      .limit(500);
    if (qErr) throw new Error(qErr.message);
    return (data ?? []) as Message[];
  }, [phone]);

  useEffect(() => {
    const t = window.setInterval(reload, POLL_MS);
    return () => window.clearInterval(t);
  }, [reload]);

  // MessageThread's mark-read path needs a client; unmatched threads update the
  // rows directly (RLS permits it) so the unread badge can be cleared here too.
  async function markRead() {
    setMarkingRead(true);
    setMarkError(null);
    try {
      const { error: updErr } = await supabase
        .from("messages")
        .update({ read_at: new Date().toISOString() })
        .is("client_id", null)
        .eq("from_phone", phone)
        .is("read_at", null);
      if (updErr) throw new Error(updErr.message);
      onRead();
    } catch (e) {
      setMarkError(e instanceof Error ? e.message : String(e));
    } finally {
      setMarkingRead(false);
    }
  }

  return (
    <div className="panel">
      <div className="page-header" style={{ marginBottom: 8 }}>
        <div>
          <h2 style={{ margin: 0 }}>{phoneDisplay(phone)}</h2>
          <div className="page-sub">Not linked to any client</div>
        </div>
        <span className="flex nowrap">
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => void markRead()}
            disabled={markingRead}
            title="Mark all messages from this number as read"
          >
            {markingRead ? "Marking…" : "Mark read"}
          </button>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setLinkOpen(true)}>
            Link to client
          </button>
        </span>
      </div>
      {markError && <div className="error-banner">Mark read failed: {markError}</div>}
      <div className="info-banner">
        This number didn&apos;t match any client&apos;s phone. Link it to a client to file these
        messages on their record and reply from here.
      </div>
      {error && <div className="error-banner">Failed to load messages: {error}</div>}
      <div className="thread" style={{ maxHeight: "50vh", minHeight: 100 }}>
        {loading && !messages ? (
          <div className="loading">
            <span className="spinner" /> Loading messages…
          </div>
        ) : (messages ?? []).length === 0 ? (
          <div className="empty-state">No messages from this number.</div>
        ) : (
          (messages ?? []).map((m) => (
            <div
              key={String(m.id)}
              className={m.direction === "out" ? "bubble bubble-out" : "bubble bubble-in"}
            >
              {m.body}
              <div className="bubble-meta">{fmtDateTime(m.created_at)}</div>
            </div>
          ))
        )}
      </div>
      <LinkClientModal
        phone={phone}
        open={linkOpen}
        onClose={() => setLinkOpen(false)}
        onLinked={(client) => {
          setLinkOpen(false);
          onLinked(client);
        }}
      />
    </div>
  );
}

export default function Inbox() {
  const [selected, setSelected] = useState<Selection | null>(null);

  const { data: rows, error, loading, reload } = useQuery<InboxRow[]>(async () => {
    const { data, error: qErr } = await supabase
      .from("v_message_inbox")
      .select("*")
      .order("last_message_at", { ascending: false });
    if (qErr) throw new Error(qErr.message);
    return (data ?? []) as InboxRow[];
  }, []);

  // 15s poll keeps the list, unread bolding, and previews fresh.
  useEffect(() => {
    const t = window.setInterval(reload, POLL_MS);
    return () => window.clearInterval(t);
  }, [reload]);

  const totalUnread = useMemo(
    () => (rows ?? []).reduce((sum, r) => sum + (r.unread_count || 0), 0),
    [rows],
  );

  function selectRow(row: InboxRow) {
    if (row.client_id != null) {
      setSelected({
        kind: "client",
        id: row.client_id,
        name: row.full_name ?? phoneDisplay(rowPhone(row)),
      });
      // MessageThread marks the thread read on open; refresh the list shortly
      // after so the bold/unread state clears without waiting for the poll.
      if (row.unread_count > 0) window.setTimeout(reload, 1500);
    } else {
      const phone = rowPhone(row);
      if (phone) setSelected({ kind: "phone", phone });
    }
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Inbox</h1>
          <div className="page-sub">
            {totalUnread > 0
              ? `${totalUnread} unread message${totalUnread === 1 ? "" : "s"}`
              : "All caught up"}
          </div>
        </div>
      </div>

      {error && <div className="error-banner">Failed to load inbox: {error}</div>}

      {loading && !rows ? (
        <div className="loading">
          <span className="spinner" /> Loading inbox…
        </div>
      ) : (rows ?? []).length === 0 ? (
        <div className="empty-state">
          No conversations yet. Incoming texts and messages you send will appear here.
        </div>
      ) : (
        <div className="inbox-split">
          <div className="inbox-list">
            {(rows ?? []).map((row) => {
              const unread = row.unread_count > 0;
              const unmatched = row.client_id == null;
              const phone = rowPhone(row);
              return (
                <button
                  key={rowKey(row)}
                  type="button"
                  className={
                    "inbox-row" +
                    (unread ? " unread" : "") +
                    (isSelected(row, selected) ? " active" : "")
                  }
                  onClick={() => selectRow(row)}
                >
                  <div className="inbox-main">
                    <div className="inbox-name">
                      {unmatched ? phoneDisplay(phone) : row.full_name ?? phoneDisplay(phone)}
                      {unmatched && (
                        <span className="badge badge-amber" style={{ marginLeft: 6 }}>
                          Unmatched
                        </span>
                      )}
                    </div>
                    <div className="inbox-preview">
                      {unmatched && <span className="muted">Unknown number — needs linking. </span>}
                      {row.last_direction === "out" ? "You: " : ""}
                      {row.last_message_body ?? ""}
                    </div>
                  </div>
                  <div className="inbox-time" title={fmtDateTime(row.last_message_at)}>
                    {shortWhen(row.last_message_at)}
                  </div>
                  {unread && <span className="unread-dot" aria-label="Unread" />}
                </button>
              );
            })}
          </div>

          {selected == null ? (
            <div className="panel">
              <div className="empty-state">Select a conversation to read and reply.</div>
            </div>
          ) : selected.kind === "client" ? (
            <ClientPane clientId={selected.id} name={selected.name} />
          ) : (
            <UnmatchedPane
              phone={selected.phone}
              onRead={reload}
              onLinked={(client) => {
                setSelected({
                  kind: "client",
                  id: client.id,
                  name: client.full_name ?? phoneDisplay(client.phone_e164),
                });
                reload();
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}
